import {
  Close as DialogClose,
  Content as DialogContent,
  Overlay as DialogOverlay,
  Portal as DialogPortal,
  Root as DialogRoot,
  Title as DialogTitle,
} from "@radix-ui/react-dialog";
import { MdArrowBack } from "react-icons/md";

import styles from "./index.module.css";

type AudioSettingsHelpDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AudioSettingsHelpDialog = ({ open, onOpenChange }: AudioSettingsHelpDialogProps) => (
  <DialogRoot onOpenChange={onOpenChange} open={open}>
    <DialogPortal>
      <DialogOverlay className={styles.dialogOverlay} />
      <DialogContent className={styles.dialogContent}>
        <div className={styles.dialogHeader}>
          <DialogClose aria-label="Close" className={styles.dialogClose}>
            <MdArrowBack color="#fff" size={20} />
          </DialogClose>
          <DialogTitle className={styles.dialogTitle}>Audio Recording Help</DialogTitle>
        </div>

        <div className={styles.dialogBody}>
          <section className={styles.helpSection}>
            <h3 className={styles.helpSectionTitle}>Loopback Recording</h3>
            <p className={styles.helpText}>
              Loopback recording allows you to capture system audio (what you hear through your
              speakers) alongside your microphone input. This is perfect for recording both sides of
              phone calls, video conferences, or any application audio.
            </p>
          </section>

          <section className={styles.helpSection}>
            <h3 className={styles.helpSectionTitle}>Setup Instructions</h3>

            <div className={styles.platformSection}>
              <h4 className={styles.platformTitle}>macOS</h4>
              <ol className={styles.instructionList}>
                <li>
                  Download and install BlackHole from{" "}
                  <a
                    href="https://existential.audio/blackhole/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    existential.audio/blackhole
                  </a>
                </li>
                <li>
                  Open <strong>Audio MIDI Setup</strong> (found in Applications/Utilities)
                </li>
                <li>
                  Click the <strong>+</strong> button and select{" "}
                  <strong>&quot;Create Multi-Output Device&quot;</strong>
                </li>
                <li>
                  Check both your <strong>speakers</strong> and <strong>BlackHole 2ch</strong>
                </li>
                <li>Set this Multi-Output Device as your system output</li>
                <li>
                  In Voyc, select <strong>BlackHole 2ch</strong> as your input device
                </li>
              </ol>
            </div>

            <div className={styles.platformSection}>
              <h4 className={styles.platformTitle}>Windows</h4>
              <ol className={styles.instructionList}>
                <li>
                  Right-click the <strong>sound icon</strong> in the system tray
                </li>
                <li>
                  Select <strong>&quot;Open Sound settings&quot;</strong>
                </li>
                <li>
                  Click <strong>&quot;Sound Control Panel&quot;</strong>
                </li>
                <li>
                  Go to the <strong>Recording</strong> tab
                </li>
                <li>
                  Right-click and select <strong>&quot;Show Disabled Devices&quot;</strong>
                </li>
                <li>
                  Find <strong>&quot;Stereo Mix&quot;</strong> and enable it
                </li>
                <li>
                  In Voyc, select <strong>Stereo Mix</strong> as your input device
                </li>
              </ol>
            </div>
          </section>

          <section className={styles.helpSection}>
            <h3 className={styles.helpSectionTitle}>Benefits</h3>
            <ul className={styles.benefitsList}>
              <li>Record both sides of phone calls</li>
              <li>Capture video conference audio</li>
              <li>Record system notifications and alerts</li>
              <li> Better quality than speakerphone recording</li>
              <li>No echo or feedback issues</li>
            </ul>
          </section>

          <section className={styles.helpSection}>
            <h3 className={styles.helpSectionTitle}>Troubleshooting</h3>
            <div className={styles.troubleshootItem}>
              <strong>No loopback device available:</strong>
              <p>
                Install the appropriate virtual audio driver for your operating system (BlackHole
                for macOS, Virtual Audio Cable for Windows).
              </p>
            </div>
            <div className={styles.troubleshootItem}>
              <strong>No audio being recorded:</strong>
              <p>
                Make sure your system audio is playing through the multi-output device that includes
                the loopback device.
              </p>
            </div>
            <div className={styles.troubleshootItem}>
              <strong>Audio quality issues:</strong>
              <p>
                Try adjusting the sample rate and buffer size in your audio settings. Match the
                sample rates between your system and the virtual device.
              </p>
            </div>
          </section>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
);
